import { useMemo } from 'react';
import { layoutToSavedPositions, useProcessLayout } from '@/api/queries/useProcessLayout';
import { useCanReadOrgCatalogs } from '@/hooks/useCanReadOrgCatalogs';
import type { ProcessForView } from '@bliks-io/ui';
import { useProcessForView } from '@/lib/useProcessForView';
import type { ProcessMapContent } from './ProcessMapBody';

/**
 * Everything the map draws, fetched for a host that does not already hold the
 * process. The result spreads onto `LazyProcessMapDialog`, which forwards it to
 * `ProcessMapBody` behind the chunk boundary.
 *
 * The process itself comes from `useProcessForView`, so this map answers "gone
 * or not yours" (404 by design), "the read failed" and "still arriving" exactly
 * as the chat views do — and gets the lookup-readiness gate that keeps a
 * diagram from painting raw ids and then flipping to names.
 *
 * **This is where the map's catalog-read rule lives.** The map is offered only
 * to a viewer who passes {@link useCanReadOrgCatalogs} (a process-scoped lookup
 * contract is the flagged follow-up that would lift the restriction). Hosts
 * read {@link UseProcessMapDialogDataResult.isReady} rather than re-deciding.
 */
export interface ProcessMapDialogGates {
  /**
   * The HOST surface's open state — the task modal or survey dialog the map
   * button sits in. That is what makes the button honest: the process read is
   * already in flight while the user reads the preview, so the button spins
   * while it runs and turns active only once the process turns out to be
   * readable.
   */
  hostOpen: boolean;
  /**
   * The MAP's own open state. Everything that feeds nothing until the diagram
   * is on screen hangs off this — see the layout query below.
   */
  mapOpen: boolean;
}

export interface UseProcessMapDialogDataResult {
  /**
   * The process is readable and the map can be offered. Hosts render their map
   * button on this — an unreadable process (404 by design for "not yours") must
   * not advertise a map that would open empty.
   */
  isReady: boolean;
  /**
   * The read that decides {@link isReady} is still running. Hosts show a busy
   * map button on this; it ends without a button when the read fails or the
   * process is not the viewer's.
   */
  isPending: boolean;
  /** Spread straight onto the dialog; `isLoading` rides along inside it. */
  content: ProcessMapContent;
}

export function useProcessMapDialogData(
  processId: string | undefined,
  { hostOpen, mapOpen }: ProcessMapDialogGates
): UseProcessMapDialogDataResult {
  const canReadOrgCatalogs = useCanReadOrgCatalogs();

  // Prefetched while the host is open for a viewer who can be OFFERED the map,
  // and fetched on demand for one who cannot but reached it another way: the
  // recording page opens its own map from the bottom bar, for whoever is
  // recording. The empty id disables the query (`useProcess` gates on `!!id`),
  // so a closed host costs nothing and a row of collapsed task cards does not
  // fan out.
  const wanted = hostOpen && (canReadOrgCatalogs || mapOpen);
  const enabledId = wanted && processId ? processId : '';
  const view = useProcessForView(enabledId, {
    withAssigneeLookup: wanted && canReadOrgCatalogs
  });
  const ready = view.status === 'ready' ? view : null;

  // Deferred to the first map-open, unlike the process: nothing reads a layout
  // until there is a diagram to arrange, and each request re-runs the whole
  // server-side involvement gate. `useProcessLayout` defaults to ENABLED, so
  // without this every host that merely mounts the hook would fetch a layout
  // for a process it may not be allowed to read.
  const layoutQuery = useProcessLayout(processId ?? '', 'published', {
    enabled: mapOpen && ready !== null
  });

  // Rebuilt only when the layout changes: a fresh array every render would
  // churn the canvas's position memo on each parent keystroke.
  const layoutData = layoutQuery.data;
  const savedPositions = useMemo(() => layoutToSavedPositions(layoutData), [layoutData]);

  const data = ready?.data;
  return {
    isReady: canReadOrgCatalogs && ready !== null,
    isPending: canReadOrgCatalogs && enabledId !== '' && view.status === 'pending',
    content: {
      // An ERRORED layout releases the gate: ELK's own arrangement is the best
      // answer left, and holding the map shut over a missing layout would be
      // worse than drawing it differently from the process page.
      isLoading: ready === null || layoutQuery.isPending,
      loadError: mapLoadError(view),
      process: data?.process ?? null,
      capabilities: data?.capabilities,
      openAssignments: data?.openAssignments,
      events: data?.events,
      notes: data?.notes,
      entitySuggestions: ready?.entitySuggestions,
      savedPositions,
      swimlaneOrder: layoutData?.swimlaneOrder,
      laneColors: layoutData?.laneColors,
      assigneeLookup: ready?.assigneeLookup
    }
  };
}

/**
 * Without this a failed query leaves `isLoading` true forever and the map spins
 * until it is closed — a hung-looking dialog is worse than a sentence.
 *
 * `unavailable` carries no retry: `show-process` answers 404 for a forbidden
 * process on purpose, so "gone" and "no longer yours" are one state, and a
 * Retry there is a button that can never succeed.
 */
function mapLoadError(view: ProcessForView): ProcessMapContent['loadError'] {
  if (view.status === 'failed') return { retry: view.retry };
  if (view.status === 'unavailable') return {};
  return undefined;
}
